import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, House, Buildings, Bank, Handshake, ChatCircleDots,
  ShieldCheck, LinkSimple, Cube, Phone, EnvelopeSimple, MapPin, Star,
} from "@phosphor-icons/react";
import FloatingHeader from "@/components/layout/FloatingHeader";
import LeadCaptureForm from "@/components/marketing/LeadCaptureForm";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`px-6 lg:px-10 max-w-7xl mx-auto ${className}`}>{children}</section>
);

const Label = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-mono text-muted-foreground border border-white/10 px-3 py-1 rounded-sm">
    <span className="w-1 h-1 bg-primary rounded-full" />{children}
  </span>
);

const programs = [
  { icon: House, name: "FHA", pitch: "Low down-payment path for first-time buyers. As little as 3.5% down and flexible credit.", stat: "3.5% down" },
  { icon: Handshake, name: "ITIN", pitch: "For borrowers using an ITIN instead of an SSN. We close them fast — often in 21 days.", stat: "21-day close" },
  { icon: ShieldCheck, name: "VA", pitch: "0% down, no PMI. Reserved for eligible veterans, service members and surviving spouses.", stat: "0% down" },
  { icon: Buildings, name: "DSCR", pitch: "Investor loans qualified on the property's income, not your personal returns.", stat: "No tax returns" },
  { icon: Bank, name: "Conventional", pitch: "The classic path — competitive rates, flexible terms, straight-line underwriting.", stat: "Rates from 5.99%" },
  { icon: Cube, name: "Jumbo & Refi", pitch: "High-balance jumbo loans and rate-and-term or cash-out refinance options.", stat: "Up to $3M" },
];

const testimonials = [
  { name: "Marcus & Elena D.", role: "First-time buyers · East Memphis", body: "We were told our ITIN would slow everything down. DW closed us in 21 days. Keys before our lease was up." },
  { name: "Priya S.", role: "DSCR investor · Nashville", body: "Four rentals, zero tax returns, one point of contact. They understand real estate operators." },
  { name: "James O.", role: "Veteran · VA loan", body: "0% down, no PMI, straight answers. This is how the process should feel." },
];

export default function Landing() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <FloatingHeader />

      {/* HERO */}
      <div className="relative pt-28 md:pt-32 pb-20 grid-bg">
        <div className="absolute inset-0 pointer-events-none noise" />
        <Section className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7 animate-fade-up">
            <Label>Licensed lender · NMLS #123456</Label>
            <h1 className="mt-6 font-heading font-black text-5xl sm:text-6xl lg:text-[86px] tracking-tighter leading-[0.92]">
              Home loans that <span className="text-primary">actually</span><br className="hidden md:block" />
              <span className="italic font-medium">move at your speed.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground font-light">
              FHA, ITIN, VA, DSCR, Jumbo, Conventional and Refi — underwritten by real humans who close.
              We built the DW Mortgage Hub intake so you can get pre-sorted in 30 seconds and skip
              the agency runaround.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <a href="#apply" data-testid="hero-apply" className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground font-medium rounded-sm hover-lift primary-glow text-sm">
                Start my application <ArrowRight size={16} weight="bold" />
              </a>
              <a href="#programs" data-testid="hero-programs" className="inline-flex items-center gap-2 px-5 py-3 border border-white/15 text-foreground rounded-sm text-sm hover-lift hover:border-white/30">
                Explore loan programs <ArrowUpRight size={14} />
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "21", u: "day close", d: "avg ITIN" },
                { k: "5.99%", u: "starting", d: "FHA 30-yr" },
                { k: "$1.4B", u: "funded", d: "since 2019" },
              ].map((s) => (
                <div key={s.k} className="border-l border-primary/40 pl-3">
                  <div className="font-heading font-black text-2xl tracking-tighter">{s.k}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-primary">{s.u}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">{s.d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <LeadCaptureForm />
          </div>
        </Section>
      </div>

      {/* Marquee */}
      <div className="border-y border-white/[0.06] py-6 overflow-hidden bg-[#080808]">
        <div className="marquee-track font-heading text-2xl md:text-3xl">
          {Array.from({ length: 2 }).map((_, k) => (
            <React.Fragment key={k}>
              <span className="text-muted-foreground">FHA loans</span><span className="text-primary">·</span>
              <span>ITIN in 21 days</span><span className="text-primary">·</span>
              <span className="text-muted-foreground">VA · 0% down</span><span className="text-primary">·</span>
              <span>DSCR rentals</span><span className="text-primary">·</span>
              <span className="text-muted-foreground">Conventional</span><span className="text-primary">·</span>
              <span>Jumbo · up to $3M</span><span className="text-primary">·</span>
              <span className="text-muted-foreground">Refi · cash-out</span><span className="text-primary">·</span>
              <span>First-time buyers welcomed</span><span className="text-primary">·</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* LOAN PROGRAMS */}
      <Section id="programs" className="py-24">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <Label>Loan programs</Label>
            <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] max-w-2xl">
              Six paths home. One team behind every close.
            </h2>
          </div>
          <a href="#apply" data-testid="programs-cta" className="text-sm inline-flex items-center gap-2 border-b border-primary text-primary hover:opacity-80">
            Find my program <ArrowUpRight size={14} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {programs.map((p) => (
            <div key={p.name} className="border border-white/[0.08] p-6 rounded-sm bg-[#080808] hover-lift hover:border-primary/40 relative">
              <div className="flex items-center justify-between">
                <p.icon size={22} className="text-primary" />
                <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-primary">{p.stat}</span>
              </div>
              <div className="mt-4 font-heading font-black text-2xl tracking-tighter">{p.name}</div>
              <p className="mt-2 text-sm text-muted-foreground">{p.pitch}</p>
              <a href="#apply" data-testid={`program-cta-${p.name.toLowerCase().split(" ")[0]}`} className="mt-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-foreground hover:text-primary">
                Apply for {p.name} <ArrowRight size={12} />
              </a>
            </div>
          ))}
        </div>
      </Section>

      {/* WHY US */}
      <Section id="why-us" className="py-24 border-t border-white/[0.06]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <Label>Why DW Mortgage Hub</Label>
            <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
              Built for people, not <span className="text-primary">paperwork.</span>
            </h2>
            <p className="mt-5 text-muted-foreground max-w-md">
              We invested in the intake so you don't get lost between social media, a call center and a
              loan officer. Every borrower is pre-sorted the moment they land — one context, one team,
              one closing.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: ShieldCheck, t: "Encrypted intake", d: "Your data rides on a signed payload from the first click to the last signature." },
              { icon: ChatCircleDots, t: "Human LOs", d: "Every application gets a licensed loan officer within 24 hours — no chatbot handoffs." },
              { icon: LinkSimple, t: "One profile everywhere", d: "Answer 3 questions once. We don't ask you the same thing at every stage." },
              { icon: House, t: "Local knowledge", d: "Neighborhood-level insight across Memphis, Nashville, Atlanta and the greater Mid-South." },
            ].map((r) => (
              <div key={r.t} className="border border-white/[0.08] p-6 rounded-sm bg-[#080808]">
                <r.icon size={22} className="text-primary" />
                <div className="mt-4 font-heading font-bold text-lg">{r.t}</div>
                <p className="mt-2 text-sm text-muted-foreground">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* PROCESS */}
      <Section id="process" className="py-24 border-t border-white/[0.06]">
        <Label>The DW process</Label>
        <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95] max-w-3xl">
          From social scroll to signed docs, without the runaround.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { n: "01", t: "Land & pre-sort", d: "Answer 3 questions — location, timeline, income type. We route your profile in seconds." },
            { n: "02", t: "Secure handoff", d: "Your encrypted payload flows into dwinvestmentgroup.com's intake — nothing re-entered." },
            { n: "03", t: "LO within 24h", d: "A licensed loan officer with your program expertise picks up the file. No queue tickets." },
            { n: "04", t: "Close & fund", d: "Docs, appraisal, underwriting and closing — coordinated by the same team from day one." },
          ].map((s) => (
            <div key={s.n} className="border border-white/[0.08] p-6 rounded-sm hover-lift hover:border-primary/40 bg-[#080808]">
              <span className="font-mono text-3xl text-primary/70">{s.n}</span>
              <div className="mt-4 font-heading font-bold text-lg leading-tight">{s.t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* STORIES */}
      <Section id="stories" className="py-24 border-t border-white/[0.06]">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <Label>Recent closes</Label>
            <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">Real people. Real closings.</h2>
          </div>
          <div className="flex items-center gap-1 text-primary">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} weight="fill" />)}
            <span className="ml-2 text-xs font-mono text-muted-foreground">4.9 / 5 · 812 reviews</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {testimonials.map((t) => (
            <div key={t.name} className="border border-white/[0.08] p-6 rounded-sm bg-[#080808] flex flex-col">
              <div className="flex gap-1 text-primary">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={12} weight="fill" />)}
              </div>
              <p className="mt-4 text-sm text-foreground/90 flex-1">"{t.body}"</p>
              <div className="mt-6 border-t border-white/[0.06] pt-4">
                <div className="font-heading font-bold text-sm">{t.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* APPLY CTA (secondary lead form spot for mid-page users) */}
      <Section id="apply" className="py-24 border-t border-white/[0.06]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Label>Ready when you are</Label>
            <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.95]">
              Start your application. <span className="text-primary">30 seconds.</span>
            </h2>
            <p className="mt-5 text-muted-foreground max-w-md">
              Pre-sort now and we'll hand your encrypted profile off to dwinvestmentgroup.com's intake —
              a real loan officer picks up the file within one business day.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              <a href="tel:+19015551234" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
                <Phone size={16} className="text-primary" /> (901) 555-1234
              </a>
              <a href="mailto:hello@dwinvestmentgroup.com" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
                <EnvelopeSimple size={16} className="text-primary" /> hello@dwinvestmentgroup.com
              </a>
              <div className="flex items-center gap-3 text-muted-foreground">
                <MapPin size={16} className="text-primary" /> Memphis · Nashville · Atlanta
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <LeadCaptureForm />
          </div>
        </div>
      </Section>

      <footer className="px-6 lg:px-10 max-w-7xl mx-auto py-12 border-t border-white/[0.06]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 border border-primary grid place-items-center"><span className="font-heading font-black text-primary text-sm">DW</span></div>
              <div>
                <div className="font-heading font-bold text-sm">DW Mortgage Hub</div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-mono">Mortgage · Lending · Advisory</div>
              </div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground max-w-sm">
              DW Mortgage Hub · NMLS #123456. Equal Housing Opportunity. Rates, terms and closing
              times vary by borrower profile and program.
            </p>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Programs</div>
            <ul className="mt-4 space-y-2 text-xs text-foreground/80">
              <li>FHA · ITIN · VA</li>
              <li>DSCR · Conventional</li>
              <li>Jumbo · Refi</li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Company</div>
            <ul className="mt-4 space-y-2 text-xs text-foreground/80">
              <li><a href="#why-us" className="hover:text-primary">About</a></li>
              <li><a href="#process" className="hover:text-primary">Process</a></li>
              <li><Link to="/login" data-testid="footer-broker-login" className="hover:text-primary">Broker console →</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-3">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">© 2026 DW Mortgage Hub · Handoff to dwinvestmentgroup.com</span>
          <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">Encrypted intake · Signed payload</span>
        </div>
      </footer>
    </div>
  );
}
